import { useMemo, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useTarefas } from "@/hooks/useTarefas";
import {
  TAREFA_PRIORIDADE_COLOR,
  TAREFA_STATUS_LABEL,
  type Tarefa,
} from "@/types/tarefas";
import { formatPrazoCurto, isPrazoAtrasado } from "./tarefasUtils";

type Props = {
  apenasMinhas: boolean;
  onOpenTarefa: (tarefa: Tarefa) => void;
  onNovaNoDia: (isoPrazo: string) => void;
};

export function TarefasCalendario({
  apenasMinhas,
  onOpenTarefa,
  onNovaNoDia,
}: Props) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selecionado, setSelecionado] = useState<Date>(() => new Date());

  const range = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return { de: start.toISOString(), ate: end.toISOString(), start, end };
  }, [cursor]);

  const { data: tarefas = [], isLoading } = useTarefas({
    apenasMinhas,
    prazoRange: { de: range.de, ate: range.ate },
  });

  const dias = useMemo(
    () => eachDayOfInterval({ start: range.start, end: range.end }),
    [range.start, range.end]
  );

  const porDia = useMemo(() => {
    const map = new Map<string, Tarefa[]>();
    for (const t of tarefas) {
      if (!t.prazo) continue;
      const key = format(parseISO(t.prazo), "yyyy-MM-dd");
      const list = map.get(key) ?? [];
      list.push(t);
      map.set(key, list);
    }
    return map;
  }, [tarefas]);

  const doDia = useMemo(() => {
    const key = format(selecionado, "yyyy-MM-dd");
    return porDia.get(key) ?? [];
  }, [porDia, selecionado]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fynix-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div className="xl:col-span-8 crm-card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="crm-title text-lg capitalize">
            {format(cursor, "MMMM yyyy", { locale: ptBR })}
          </h3>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => setCursor((c) => subMonths(c, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full h-8"
              onClick={() => {
                const hoje = new Date();
                setCursor(startOfMonth(hoje));
                setSelecionado(hoje);
              }}
            >
              Hoje
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => setCursor((c) => addMonths(c, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
            <div key={d} className="text-center text-[11px] font-medium text-fynix-muted py-1">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {dias.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const items = porDia.get(key) ?? [];
            const noMes = isSameMonth(day, cursor);
            const sel = isSameDay(day, selecionado);
            const hoje = isSameDay(day, new Date());
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelecionado(day)}
                onDoubleClick={() => {
                  const prazo = new Date(day);
                  prazo.setHours(9, 0, 0, 0);
                  onNovaNoDia(prazo.toISOString());
                }}
                className={`min-h-[72px] rounded-xl border p-1.5 text-left transition-colors ${
                  sel
                    ? "border-fynix-brand bg-fynix-brand-soft/40"
                    : "border-transparent hover:bg-muted/50"
                } ${noMes ? "bg-white" : "bg-transparent opacity-40"}`}
              >
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                    hoje ? "bg-fynix-brand text-white" : "text-fynix-ink"
                  }`}
                >
                  {format(day, "d")}
                </span>
                <div className="mt-1 space-y-0.5">
                  {items.slice(0, 2).map((t) => (
                    <div
                      key={t.id}
                      className={`truncate rounded px-1 text-[10px] leading-4 ${
                        isPrazoAtrasado(t)
                          ? "bg-red-100 text-red-700"
                          : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {t.titulo}
                    </div>
                  ))}
                  {items.length > 2 && (
                    <div className="text-[10px] text-fynix-muted px-1">
                      +{items.length - 2}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-fynix-muted">
          Clique duplo em um dia para criar tarefa. Use as setas para navegar o mês.
        </p>
      </div>

      <div className="xl:col-span-4 crm-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-fynix-muted">Dia</p>
            <h3 className="crm-title text-base capitalize">
              {format(selecionado, "EEEE, d MMM", { locale: ptBR })}
            </h3>
          </div>
          <Button
            type="button"
            size="sm"
            className="rounded-xl"
            onClick={() => {
              const prazo = new Date(selecionado);
              prazo.setHours(9, 0, 0, 0);
              onNovaNoDia(prazo.toISOString());
            }}
          >
            Nova
          </Button>
        </div>
        <div className="space-y-2 max-h-[480px] overflow-y-auto">
          {doDia.length === 0 ? (
            <p className="text-sm text-fynix-muted py-8 text-center">
              Nenhuma tarefa neste dia
            </p>
          ) : (
            doDia.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onOpenTarefa(t)}
                className="w-full text-left rounded-xl border border-fynix-line p-3 hover:bg-muted/40"
              >
                <p className="text-sm font-semibold line-clamp-2">{t.titulo}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                  <span
                    className={`text-[11px] ${
                      isPrazoAtrasado(t) ? "text-red-600 font-medium" : "text-fynix-muted"
                    }`}
                  >
                    {formatPrazoCurto(t.prazo)}
                  </span>
                  <Badge variant="outline" className="text-[10px]">
                    {TAREFA_STATUS_LABEL[t.status]}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${TAREFA_PRIORIDADE_COLOR[t.prioridade]}`}
                  >
                    {t.prioridade}
                  </Badge>
                </div>
                {t.cliente?.nome && (
                  <p className="mt-1 text-[11px] text-fynix-muted">{t.cliente.nome}</p>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
